import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import * as THREE from 'three';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import Icone from '../components/Icones';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export interface TecnicoLocalizacao {
  tecnico_id: number;
  tecnico_nome: string;
  tecnico_email?: string | null;
  latitude: number;
  longitude: number;
  precisao_metros?: number | null;
  coletado_em: string;
  os_codigo?: string | null;
  os_tipo?: string | null;
  os_status?: string | null;
}

export interface TecnicoHistoricoPonto {
  latitude: number;
  longitude: number;
  precisao_metros?: number | null;
  coletado_em: string;
}

export interface TecnicoHistorico {
  tecnico_id: number;
  tecnico_nome: string;
  tecnico_email?: string | null;
  pontos: TecnicoHistoricoPonto[];
}

interface TecnicoEmpresaResumo {
  id: number;
  nome: string;
  email?: string | null;
  status: string;
}

interface TecnicoDisponivelMapa {
  tecnico_id: number;
  tecnico_nome: string;
  tecnico_email?: string | null;
  os_codigo?: string | null;
  os_tipo?: string | null;
  status: string;
  possui_localizacao: boolean;
}

export interface QuilometragemResumo {
  hojeKm: number;
  totalKm: number;
  segmentosValidos: number;
  segmentosIgnorados: number;
}

type CamadaOpcional = 'rota' | 'velocidade';

export const INTERVALO_PADRAO_SEGUNDOS = 15;
export const INTERVALO_MIN_SEGUNDOS = 1;
export const INTERVALO_MAX_SEGUNDOS = 15;
export const DISTANCIA_MINIMA_SEGMENTO_KM = 0.03;
export const VELOCIDADE_MAXIMA_SEGMENTO_KMH = 160;

const CORES_TECNICOS = ['#2563eb', '#0f766e', '#d97706', '#7c3aed', '#db2777', '#0891b2', '#65a30d', '#ea580c'];

function dataHoraCurta(valor: string): string {
  try {
    return new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return valor || '-';
  }
}

function idadeLocalizacao(valor: string): string {
  const t = new Date(valor).getTime();
  if (!Number.isFinite(t)) return '-';
  const min = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min atrás`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}min atrás`;
}

function inicioDoDiaLocal(referencia: Date): Date {
  return new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate(), 0, 0, 0, 0);
}

function pontoNoMesmoDiaLocal(valor: string, referencia: Date): boolean {
  const data = new Date(valor);
  if (!Number.isFinite(data.getTime())) return false;
  const inicio = inicioDoDiaLocal(referencia);
  const fim = new Date(inicio);
  fim.setDate(fim.getDate() + 1);
  return data >= inicio && data < fim;
}

function mapaHtml(valor?: string | null): string {
  return String(valor || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c] || c));
}

function coordenadasValidas(latitude: number, longitude: number): boolean {
  return Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
}

function coordenadaValida(p: TecnicoLocalizacao): boolean {
  return coordenadasValidas(Number(p.latitude), Number(p.longitude));
}

function pontoHistoricoValido(p: TecnicoHistoricoPonto): boolean {
  return coordenadasValidas(Number(p.latitude), Number(p.longitude));
}

export function normalizarIntervaloAtualizacao(valor: number): number {
  const numero = Number.isFinite(valor) ? Math.round(valor) : INTERVALO_PADRAO_SEGUNDOS;
  return Math.min(INTERVALO_MAX_SEGUNDOS, Math.max(INTERVALO_MIN_SEGUNDOS, numero));
}

function chavePonto(ponto: { latitude: number; longitude: number; coletado_em: string }): string {
  return `${Number(ponto.latitude).toFixed(7)}|${Number(ponto.longitude).toFixed(7)}|${ponto.coletado_em}`;
}

function ordenarPontos(pontos: TecnicoHistoricoPonto[]): TecnicoHistoricoPonto[] {
  return [...pontos].sort((a, b) => {
    const ta = new Date(a.coletado_em).getTime();
    const tb = new Date(b.coletado_em).getTime();
    if (ta !== tb) return ta - tb;
    if (a.latitude !== b.latitude) return a.latitude - b.latitude;
    return a.longitude - b.longitude;
  });
}

export function mesclarHistoricoComUltimas(
  historico: TecnicoHistorico[],
  ultimas: TecnicoLocalizacao[],
): TecnicoHistorico[] {
  const mapa = new Map<number, TecnicoHistorico>();
  const chavesPorTecnico = new Map<number, Set<string>>();

  historico.forEach((item) => {
    const pontosOrdenados = ordenarPontos(item.pontos || []);
    mapa.set(item.tecnico_id, {
      tecnico_id: item.tecnico_id,
      tecnico_nome: item.tecnico_nome,
      tecnico_email: item.tecnico_email ?? null,
      pontos: pontosOrdenados,
    });
    chavesPorTecnico.set(item.tecnico_id, new Set(pontosOrdenados.map(chavePonto)));
  });

  ultimas.forEach((loc) => {
    if (!coordenadaValida(loc)) return;
    const grupo = mapa.get(loc.tecnico_id) || {
      tecnico_id: loc.tecnico_id,
      tecnico_nome: loc.tecnico_nome,
      tecnico_email: loc.tecnico_email ?? null,
      pontos: [],
    };

    if (!mapa.has(loc.tecnico_id)) {
      mapa.set(loc.tecnico_id, grupo);
      chavesPorTecnico.set(loc.tecnico_id, new Set());
    }

    grupo.tecnico_nome = loc.tecnico_nome || grupo.tecnico_nome;
    grupo.tecnico_email = loc.tecnico_email ?? grupo.tecnico_email ?? null;

    const ponto: TecnicoHistoricoPonto = {
      latitude: Number(loc.latitude),
      longitude: Number(loc.longitude),
      precisao_metros: loc.precisao_metros ?? null,
      coletado_em: loc.coletado_em,
    };
    const chave = chavePonto(ponto);
    const chaves = chavesPorTecnico.get(loc.tecnico_id) as Set<string>;
    if (!chaves.has(chave)) {
      grupo.pontos = [...grupo.pontos, ponto];
      chaves.add(chave);
    }
    grupo.pontos = ordenarPontos(grupo.pontos);
  });

  return Array.from(mapa.values()).sort((a, b) => a.tecnico_nome.localeCompare(b.tecnico_nome, 'pt-BR'));
}

function substituirHistoricosPorTecnico(atual: TecnicoHistorico[], novos: TecnicoHistorico[]): TecnicoHistorico[] {
  const mapa = new Map<number, TecnicoHistorico>();
  atual.forEach((item) => mapa.set(item.tecnico_id, item));
  novos.forEach((item) => mapa.set(item.tecnico_id, item));
  return Array.from(mapa.values()).sort((a, b) => a.tecnico_nome.localeCompare(b.tecnico_nome, 'pt-BR'));
}

function distanciaKm(a: TecnicoHistoricoPonto, b: TecnicoHistoricoPonto): number {
  const lat1 = (Number(a.latitude) * Math.PI) / 180;
  const lat2 = (Number(b.latitude) * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((Number(b.longitude) - Number(a.longitude)) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function calcularVelocidadeKmh(pontos: TecnicoHistoricoPonto[]): number | null {
  const validos = ordenarPontos((pontos || []).filter(pontoHistoricoValido));
  if (validos.length < 2) return null;

  for (let i = validos.length - 1; i > 0; i -= 1) {
    const atual = validos[i];
    const anterior = validos[i - 1];
    const deltaMs = new Date(atual.coletado_em).getTime() - new Date(anterior.coletado_em).getTime();
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) continue;
    const distancia = distanciaKm(anterior, atual);
    const velocidade = distancia / (deltaMs / 3600000);
    if (!Number.isFinite(velocidade)) continue;
    return Math.max(0, velocidade);
  }

  return null;
}

export function calcularQuilometragemPontos(
  pontos: TecnicoHistoricoPonto[],
  referenciaHoje: Date = new Date(),
): QuilometragemResumo {
  const validos = ordenarPontos((pontos || []).filter(pontoHistoricoValido));
  let hojeKm = 0;
  let totalKm = 0;
  let segmentosValidos = 0;
  let segmentosIgnorados = 0;

  for (let i = 1; i < validos.length; i += 1) {
    const anterior = validos[i - 1];
    const atual = validos[i];
    const deltaMs = new Date(atual.coletado_em).getTime() - new Date(anterior.coletado_em).getTime();
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
      segmentosIgnorados += 1;
      continue;
    }

    const distancia = distanciaKm(anterior, atual);
    const velocidade = distancia / (deltaMs / 3600000);
    if (
      !Number.isFinite(distancia)
      || distancia < DISTANCIA_MINIMA_SEGMENTO_KM
      || !Number.isFinite(velocidade)
      || velocidade > VELOCIDADE_MAXIMA_SEGMENTO_KMH
    ) {
      segmentosIgnorados += 1;
      continue;
    }

    totalKm += distancia;
    if (pontoNoMesmoDiaLocal(atual.coletado_em, referenciaHoje)) {
      hojeKm += distancia;
    }
    segmentosValidos += 1;
  }

  return { hojeKm, totalKm, segmentosValidos, segmentosIgnorados };
}

function textoVelocidade(velocidade: number | null): string {
  if (velocidade === null) return 'Sem dados';
  return `${velocidade.toFixed(1)} km/h`;
}

function statusTecnicoLabel(status: string): string {
  return status === 'inativo' ? 'Inativo' : 'Ativo';
}

function textoKm(valor: number): string {
  return `${valor.toFixed(1)} km`;
}

function pontosDeRota(item?: TecnicoHistorico | null): [number, number][] {
  return (item?.pontos || [])
    .filter(pontoHistoricoValido)
    .map((ponto) => [Number(ponto.latitude), Number(ponto.longitude)] as [number, number]);
}

function corTecnicoPorIndice(indice: number): string {
  return CORES_TECNICOS[indice % CORES_TECNICOS.length];
}

function Stat({ icone, titulo, valor, detalhe, cor }: { icone: string; titulo: string; valor: string | number; detalhe: string; cor: string }) {
  return (
    <div className="stat">
      <div className="stat-ic" style={{ background: `${cor}1f`, color: cor }}><Icone nome={icone} size={24} /></div>
      <div>
        <div className="stat-v" style={{ color: cor }}>{valor}</div>
        <div className="stat-l">{titulo}</div>
        <div style={{ color: 'var(--texto-suave)', fontSize: 12 }}>{detalhe}</div>
      </div>
    </div>
  );
}

function LoadingMapa({
  compacto,
  titulo,
  detalhe,
}: {
  compacto?: boolean;
  titulo: string;
  detalhe: string;
}) {
  return (
    <div className={compacto ? 'mapa-loading-badge' : 'mapa-loading-overlay'} aria-live="polite" aria-busy="true">
      <div className={compacto ? 'mapa-loading-badge-card' : 'mapa-loading-card'}>
        <div className="mapa-loading-orbs" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <strong>{titulo}</strong>
        <span>{detalhe}</span>
      </div>
    </div>
  );
}

function LoadingTelaInteira({ titulo, detalhe }: { titulo: string; detalhe: string }) {
  const sceneRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sceneRef.current) return;
    // A cena 3D e so um enfeite do loading: se o navegador nao tiver WebGL
    // disponivel (driver, extensao, ambiente sem GPU) isso NAO pode derrubar a
    // tela inteira — sem try/catch, uma excecao aqui suja o efeito e, sem
    // Error Boundary no app, apaga a pagina toda em branco.
    try {

    const container = sceneRef.current;
    const scene = new THREE.Scene();
    scene.background = null;

    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0.15, 7.6);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0xffffff, 1.55);
    scene.add(ambient);

    const rim = new THREE.DirectionalLight(0x7aa6ff, 2.4);
    rim.position.set(4.5, 3.5, 6);
    scene.add(rim);

    const fill = new THREE.PointLight(0x22d3ee, 18, 20, 2);
    fill.position.set(-3, -1.5, 4);
    scene.add(fill);

    const blob = (ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot: number, color: string) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(-rx * 0.9, -ry * 0.1);
      ctx.bezierCurveTo(-rx * 1.1, -ry * 0.9, rx * 0.2, -ry * 1.2, rx * 0.95, -ry * 0.35);
      ctx.bezierCurveTo(rx * 1.2, ry * 0.2, rx * 0.7, ry * 1.15, -rx * 0.25, ry * 0.95);
      ctx.bezierCurveTo(-rx * 1.15, ry * 0.6, -rx * 1.2, ry * 0.15, -rx * 0.9, -ry * 0.1);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    };

    const criarTexturaTerra = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const fundo = ctx.createLinearGradient(0, 0, 1024, 512);
      fundo.addColorStop(0, '#0a49c2');
      fundo.addColorStop(0.48, '#0f6bd7');
      fundo.addColorStop(1, '#1497c8');
      ctx.fillStyle = fundo;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.globalAlpha = 0.24;
      for (let i = 0; i < 18; i += 1) {
        const brilho = ctx.createRadialGradient(
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          8,
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          120,
        );
        brilho.addColorStop(0, '#9ed7ff');
        brilho.addColorStop(1, 'transparent');
        ctx.fillStyle = brilho;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.globalAlpha = 1;

      const corTerra = '#79c94f';
      const corTerraEscura = '#4f9a35';
      blob(ctx, 180, 160, 150, 90, -0.15, corTerra);
      blob(ctx, 290, 265, 95, 65, 0.4, corTerraEscura);
      blob(ctx, 435, 190, 125, 72, -0.32, corTerra);
      blob(ctx, 585, 300, 150, 82, 0.14, corTerraEscura);
      blob(ctx, 725, 150, 110, 68, -0.24, corTerra);
      blob(ctx, 860, 250, 135, 76, 0.26, corTerraEscura);
      blob(ctx, 930, 380, 70, 40, -0.18, corTerra);

      ctx.globalAlpha = 0.18;
      ctx.fillStyle = '#d7f7c8';
      for (let i = 0; i < 20; i += 1) {
        blob(
          ctx,
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          34 + Math.random() * 60,
          14 + Math.random() * 28,
          (Math.random() - 0.5) * 1.2,
          '#d7f7c8',
        );
      }
      ctx.globalAlpha = 1;

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    };

    const criarTexturaNuvem = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < 24; i += 1) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const grad = ctx.createRadialGradient(x, y, 8, x, y, 68 + Math.random() * 38);
        grad.addColorStop(0, 'rgba(255,255,255,0.86)');
        grad.addColorStop(0.45, 'rgba(255,255,255,0.42)');
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(x - 110, y - 110, 220, 220);
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    };

    const grupo = new THREE.Group();
    grupo.position.y = 0.2;
    scene.add(grupo);

    const terra = new THREE.Mesh(
      new THREE.SphereGeometry(1.72, 64, 64),
      new THREE.MeshStandardMaterial({
        map: criarTexturaTerra() || undefined,
        color: 0xffffff,
        roughness: 0.86,
        metalness: 0.04,
        emissive: 0x0b2d74,
        emissiveIntensity: 0.15,
      }),
    );
    grupo.add(terra);

    const nuvens = new THREE.Mesh(
      new THREE.SphereGeometry(1.78, 48, 48),
      new THREE.MeshStandardMaterial({
        map: criarTexturaNuvem() || undefined,
        color: 0xffffff,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
      }),
    );
    grupo.add(nuvens);

    const atmosfera = new THREE.Mesh(
      new THREE.SphereGeometry(1.95, 48, 48),
      new THREE.MeshBasicMaterial({
        color: 0x7fd4ff,
        transparent: true,
        opacity: 0.12,
        side: THREE.BackSide,
      }),
    );
    grupo.add(atmosfera);

    const orbita = new THREE.Mesh(
      new THREE.TorusGeometry(2.58, 0.045, 22, 180),
      new THREE.MeshBasicMaterial({ color: 0x7bd9ff, transparent: true, opacity: 0.78 }),
    );
    orbita.rotation.x = Math.PI / 2.4;
    orbita.rotation.z = 0.28;
    grupo.add(orbita);

    const marcadorMaterial = new THREE.MeshBasicMaterial({ color: 0xfff4b5 });
    const marcadorGeo = new THREE.SphereGeometry(0.06, 16, 16);
    const marcadorA = new THREE.Mesh(marcadorGeo, marcadorMaterial);
    marcadorA.position.set(0.72, 1.18, 1.04);
    grupo.add(marcadorA);

    const marcadorB = new THREE.Mesh(marcadorGeo, marcadorMaterial);
    marcadorB.position.set(-1.08, -0.3, 1.22);
    grupo.add(marcadorB);

    const marcadorC = new THREE.Mesh(marcadorGeo, marcadorMaterial);
    marcadorC.position.set(1.3, -0.62, 0.72);
    grupo.add(marcadorC);

    const particulasGeo = new THREE.BufferGeometry();
    const particulas = new Float32Array(180);
    for (let i = 0; i < particulas.length; i += 3) {
      const raio = 3.4 + Math.random() * 2;
      const angulo = Math.random() * Math.PI * 2;
      const altura = (Math.random() - 0.5) * 3.6;
      particulas[i] = Math.cos(angulo) * raio;
      particulas[i + 1] = Math.sin(angulo) * raio * 0.65;
      particulas[i + 2] = altura;
    }
    particulasGeo.setAttribute('position', new THREE.BufferAttribute(particulas, 3));
    const poeira = new THREE.Points(
      particulasGeo,
      new THREE.PointsMaterial({ color: 0xb8d7ff, size: 0.06, transparent: true, opacity: 0.88 }),
    );
    scene.add(poeira);

    const resize = () => {
      const width = container.clientWidth || 320;
      const height = container.clientHeight || 320;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    resize();
    window.addEventListener('resize', resize);

    const clock = new THREE.Clock();
    let frame = 0;
    const render = () => {
      const t = clock.getElapsedTime();
      grupo.rotation.x = 0.42 + Math.sin(t * 0.45) * 0.08;
      grupo.rotation.y += 0.0048;
      grupo.rotation.z = -0.22 + Math.sin(t * 0.32) * 0.04;
      terra.rotation.y += 0.0038;
      nuvens.rotation.y += 0.0056;
      orbita.rotation.z -= 0.006;
      atmosfera.scale.setScalar(1 + Math.sin(t * 1.6) * 0.01);
      marcadorA.scale.setScalar(1 + Math.sin(t * 2.2) * 0.18);
      marcadorB.scale.setScalar(1 + Math.sin(t * 1.8 + 1.4) * 0.16);
      marcadorC.scale.setScalar(1 + Math.sin(t * 1.9 + 0.5) * 0.14);
      poeira.rotation.z += 0.0014;
      poeira.rotation.y = Math.sin(t * 0.3) * 0.18;
      renderer.render(scene, camera);
      frame = window.requestAnimationFrame(render);
    };

    render();

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      renderer.dispose();
      terra.geometry.dispose();
      (terra.material as any).dispose();
      nuvens.geometry.dispose();
      (nuvens.material as any).dispose();
      atmosfera.geometry.dispose();
      (atmosfera.material as any).dispose();
      orbita.geometry.dispose();
      (orbita.material as any).dispose();
      marcadorGeo.dispose();
      marcadorMaterial.dispose();
      particulasGeo.dispose();
      (poeira.material as any).dispose();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
    };
    } catch (erroWebgl) {
      console.error('[rastreamento] globo 3D indisponivel, seguindo sem a animacao:', erroWebgl);
      return undefined;
    }
  }, []);

  return (
    <div className="rastreamento-loading-overlay" aria-live="polite" aria-busy="true">
      <div className="rastreamento-loading-card">
        <div className="rastreamento-loading-scene" ref={sceneRef} aria-hidden="true" />
        <strong>{titulo}</strong>
        <span>{detalhe}</span>
      </div>
    </div>
  );
}

function MapaTecnicos({
  pontos,
  historico,
  mostrarRota,
  mostrarVelocidade,
  corPorTecnico,
  fullscreenAtivo,
  carregandoInicial,
  carregandoComplementar,
  tituloCarregamento,
  detalheCarregamento,
}: {
  pontos: TecnicoLocalizacao[];
  historico: TecnicoHistorico[];
  mostrarRota: boolean;
  mostrarVelocidade: boolean;
  corPorTecnico: Map<number, string>;
  fullscreenAtivo: boolean;
  carregandoInicial: boolean;
  carregandoComplementar: boolean;
  tituloCarregamento: string;
  detalheCarregamento: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<number, L.Marker>>(new Map());
  const rotasRef = useRef<Map<number, L.Polyline>>(new Map());
  const autoFocoInicialRef = useRef(false);
  const pontosValidos = useMemo(() => pontos.filter(coordenadaValida), [pontos]);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;

    const centro: [number, number] = pontosValidos[0]
      ? [Number(pontosValidos[0].latitude), Number(pontosValidos[0].longitude)]
      : [-23.5505, -46.6333];
    const mapaCriado = L.map(ref.current, { zoomControl: true, preferCanvas: true });
    if (!mapaCriado || typeof (mapaCriado as any).setView !== 'function') return;
    mapRef.current = mapaCriado.setView(centro, pontosValidos.length ? 13 : 5);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(mapRef.current);

    const ajustar = () => mapRef.current?.invalidateSize();
    requestAnimationFrame(ajustar);
    const t1 = window.setTimeout(ajustar, 120);
    const t2 = window.setTimeout(ajustar, 450);
    window.addEventListener('resize', ajustar);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener('resize', ajustar);
    };
  }, [pontosValidos]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const ajustar = () => map.invalidateSize();
    const t1 = window.setTimeout(ajustar, 30);
    const t2 = window.setTimeout(ajustar, 220);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [fullscreenAtivo]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const historicoPorTecnico = new Map<number, TecnicoHistorico>();
    historico.forEach((item) => historicoPorTecnico.set(item.tecnico_id, item));

    const bounds: [number, number][] = [];
    const marcadoresAtivos = new Set<number>();
    const rotasAtivas = new Set<number>();

    pontosValidos.forEach((ponto) => {
      const posicao: [number, number] = [Number(ponto.latitude), Number(ponto.longitude)];
      const historicoTecnico = historicoPorTecnico.get(ponto.tecnico_id) || null;
      const rota = pontosDeRota(historicoTecnico);
      const velocidade = mostrarVelocidade ? calcularVelocidadeKmh(historicoTecnico?.pontos || []) : null;
      let popup = `<strong>${mapaHtml(ponto.tecnico_nome)}</strong><br>${mapaHtml(ponto.os_codigo || 'Sem OS em andamento')}<br>${mapaHtml(dataHoraCurta(ponto.coletado_em))}<br>${mapaHtml(idadeLocalizacao(ponto.coletado_em))}`;
      if (mostrarVelocidade) popup += `<br>${mapaHtml(`Velocidade: ${textoVelocidade(velocidade)}`)}`;

      let marker = markersRef.current.get(ponto.tecnico_id);
      if (!marker) {
        marker = L.marker(posicao, { title: ponto.tecnico_nome }).addTo(map);
        markersRef.current.set(ponto.tecnico_id, marker);
      } else if ((marker as any).setLatLng) {
        (marker as any).setLatLng(posicao);
      }

      marker.bindPopup(popup);
      if (mostrarVelocidade && velocidade !== null) {
        marker.bindTooltip(textoVelocidade(velocidade), {
          permanent: true,
          direction: 'top',
          offset: [0, -18],
          className: 'mapa-speed-tooltip',
        });
      } else if ((marker as any).unbindTooltip) {
        (marker as any).unbindTooltip();
      }

      marcadoresAtivos.add(ponto.tecnico_id);
      bounds.push(posicao);

      if (mostrarRota && rota.length > 1) {
        const cor = corPorTecnico.get(ponto.tecnico_id) || '#2563eb';
        let polyline = rotasRef.current.get(ponto.tecnico_id);
        if (!polyline) {
          polyline = L.polyline(rota, {
            color: cor,
            weight: 4,
            opacity: 0.72,
          }).addTo(map);
          rotasRef.current.set(ponto.tecnico_id, polyline);
        } else {
          if ((polyline as any).setLatLngs) (polyline as any).setLatLngs(rota);
          if ((polyline as any).setStyle) (polyline as any).setStyle({ color: cor });
        }
        rotasAtivas.add(ponto.tecnico_id);
        rota.forEach((coordenada) => bounds.push(coordenada));
      }
    });

    Array.from(markersRef.current.entries()).forEach(([tecnicoId, marker]) => {
      if (marcadoresAtivos.has(tecnicoId)) return;
      marker.remove();
      markersRef.current.delete(tecnicoId);
    });

    Array.from(rotasRef.current.entries()).forEach(([tecnicoId, rota]) => {
      if (rotasAtivas.has(tecnicoId)) return;
      rota.remove();
      rotasRef.current.delete(tecnicoId);
    });

    const precisaFocar = !autoFocoInicialRef.current;
    if (precisaFocar && bounds.length > 1) {
      if ((map as any).flyToBounds) (map as any).flyToBounds(bounds, { padding: [70, 70], maxZoom: 15, duration: 0.65 });
      else map.fitBounds(bounds, { padding: [70, 70], maxZoom: 15 });
      autoFocoInicialRef.current = true;
    } else if (precisaFocar && bounds.length === 1) {
      if ((map as any).flyTo) (map as any).flyTo(bounds[0], 15, { duration: 0.65 });
      else map.setView(bounds[0], 15);
      autoFocoInicialRef.current = true;
    }

  }, [corPorTecnico, historico, mostrarRota, mostrarVelocidade, pontosValidos]);

  useEffect(() => () => {
    markersRef.current.forEach((marker) => marker.remove());
    rotasRef.current.forEach((rota) => rota.remove());
    markersRef.current.clear();
    rotasRef.current.clear();
    mapRef.current?.remove();
    mapRef.current = null;
  }, []);

  return (
    <div className="mapa-shell">
      <div className="mapa-tecnicos" ref={ref} />
      {carregandoInicial && (
        <LoadingMapa titulo={tituloCarregamento} detalhe={detalheCarregamento} />
      )}
      {!carregandoInicial && carregandoComplementar && (
        <LoadingMapa compacto titulo={tituloCarregamento} detalhe={detalheCarregamento} />
      )}
      {!pontosValidos.length && (
        <div className="mapa-empty">
          Nenhuma coordenada válida recebida ainda. A tabela abaixo continua mostrando os dados enviados pelo app.
        </div>
      )}
    </div>
  );
}

export default function RastreamentoTecnicos() {
  const { user } = useAuth();
  const [locs, setLocs] = useState<TecnicoLocalizacao[]>([]);
  const [tecnicosEmpresa, setTecnicosEmpresa] = useState<TecnicoEmpresaResumo[]>([]);
  const [historico, setHistorico] = useState<TecnicoHistorico[]>([]);
  const [camadasSelecionadas, setCamadasSelecionadas] = useState<CamadaOpcional[]>(['rota']);
  const [tecnicosSelecionados, setTecnicosSelecionados] = useState<number[]>([]);
  const [intervaloSegundos, setIntervaloSegundos] = useState(INTERVALO_PADRAO_SEGUNDOS);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [historicoIdsCarregados, setHistoricoIdsCarregados] = useState<number[]>([]);
  const [historicoIdsCarregando, setHistoricoIdsCarregando] = useState<number[]>([]);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [mapaTelaCheia, setMapaTelaCheia] = useState(false);
  const carregarUltimasRef = useRef<() => Promise<void>>(async () => {});
  const inFlightUltimasRef = useRef(false);
  const mapaCardRef = useRef<HTMLDivElement | null>(null);
  const locsRef = useRef<TecnicoLocalizacao[]>([]);
  const selecaoInicializadaRef = useRef(false);
  const historicoIdsCarregadosRef = useRef<Set<number>>(new Set());
  const historicoIdsCarregandoRef = useRef<Set<number>>(new Set());
  const podeRastrear = Boolean(
    user?.rastreamento_tecnico_habilitado
    && (user?.perfil === 'admin_geral' || user?.pode_ver_localizacao_tecnico),
  );
  const mostrarRota = camadasSelecionadas.includes('rota');
  const mostrarVelocidade = camadasSelecionadas.includes('velocidade');

  useEffect(() => {
    locsRef.current = locs;
  }, [locs]);

  useEffect(() => {
    const aoMudarFullscreen = () => {
      const ativo = document.fullscreenElement === mapaCardRef.current;
      setMapaTelaCheia(ativo);
    };
    document.addEventListener('fullscreenchange', aoMudarFullscreen);
    return () => {
      document.removeEventListener('fullscreenchange', aoMudarFullscreen);
    };
  }, []);

  const tecnicosDisponiveis = useMemo(() => {
    const locsPorTecnico = new Map<number, TecnicoLocalizacao>();
    locs.forEach((loc) => {
      locsPorTecnico.set(loc.tecnico_id, loc);
    });

    const mapa = new Map<number, TecnicoDisponivelMapa>();
    tecnicosEmpresa.forEach((tecnico) => {
      const loc = locsPorTecnico.get(tecnico.id);
      mapa.set(tecnico.id, {
        tecnico_id: tecnico.id,
        tecnico_nome: tecnico.nome,
        tecnico_email: tecnico.email ?? null,
        os_codigo: loc?.os_codigo ?? null,
        os_tipo: loc?.os_tipo ?? null,
        status: tecnico.status || 'ativo',
        possui_localizacao: Boolean(loc),
      });
    });

    locs.forEach((loc) => {
      if (mapa.has(loc.tecnico_id)) return;
      mapa.set(loc.tecnico_id, {
        tecnico_id: loc.tecnico_id,
        tecnico_nome: loc.tecnico_nome,
        tecnico_email: loc.tecnico_email ?? null,
        os_codigo: loc.os_codigo ?? null,
        os_tipo: loc.os_tipo ?? null,
        status: 'ativo',
        possui_localizacao: true,
      });
    });

    return Array.from(mapa.values()).sort((a, b) => a.tecnico_nome.localeCompare(b.tecnico_nome, 'pt-BR'));
  }, [locs, tecnicosEmpresa]);

  useEffect(() => {
    const idsDisponiveis = tecnicosDisponiveis.map((item) => item.tecnico_id);
    if (!idsDisponiveis.length) {
      setTecnicosSelecionados([]);
      selecaoInicializadaRef.current = false;
      return;
    }

    setTecnicosSelecionados((atual) => {
      const validos = atual.filter((id) => idsDisponiveis.includes(id));
      if (!selecaoInicializadaRef.current || !validos.length) {
        selecaoInicializadaRef.current = true;
        return idsDisponiveis;
      }
      return validos;
    });
  }, [tecnicosDisponiveis]);

  useEffect(() => {
    if (!podeRastrear) return;
    let ativo = true;

    async function carregarUltimas() {
      if (!ativo || document.visibilityState === 'hidden' || inFlightUltimasRef.current) return;
      inFlightUltimasRef.current = true;
      setErro('');
      setCarregando(true);
      try {
        const [dados, tecnicos] = await Promise.all([
          api<TecnicoLocalizacao[]>('/dashboard/tecnicos_localizacao.php'),
          api<TecnicoEmpresaResumo[]>('/cadastros/tecnicos.php'),
        ]);
        if (!ativo) return;
        setLocs(dados);
        setTecnicosEmpresa(tecnicos);
        setAtualizadoEm(new Date());
        if (historicoIdsCarregadosRef.current.size) {
          setHistorico((anterior) => mesclarHistoricoComUltimas(anterior, dados));
        }
      } catch (e) {
        if (ativo) setErro(e instanceof Error ? e.message : String(e));
      } finally {
        if (ativo) setCarregando(false);
        inFlightUltimasRef.current = false;
      }
    }

    carregarUltimasRef.current = carregarUltimas;
    void carregarUltimas();
    const timer = window.setInterval(() => { void carregarUltimas(); }, normalizarIntervaloAtualizacao(intervaloSegundos) * 1000);
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void carregarUltimas();
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      ativo = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [intervaloSegundos, podeRastrear]);

  useEffect(() => {
    if (!podeRastrear || !tecnicosSelecionados.length) return;
    const idsPendentes = tecnicosSelecionados.filter((tecnicoId) => (
      !historicoIdsCarregadosRef.current.has(tecnicoId)
      && !historicoIdsCarregandoRef.current.has(tecnicoId)
    ));
    if (!idsPendentes.length) return;

    let ativo = true;
    idsPendentes.forEach((tecnicoId) => historicoIdsCarregandoRef.current.add(tecnicoId));
    setHistoricoIdsCarregando(Array.from(historicoIdsCarregandoRef.current));

    async function carregarHistoricoSelecionado() {
      setErro('');
      try {
        const dados = await api<TecnicoHistorico[]>(
          `/dashboard/tecnicos_localizacao_historico.php?tecnico_ids=${idsPendentes.join(',')}`,
        );
        if (!ativo) return;
        const historicosNormalizados = mesclarHistoricoComUltimas(dados, locsRef.current)
          .filter((item) => idsPendentes.includes(item.tecnico_id));
        setHistorico((anterior) => substituirHistoricosPorTecnico(anterior, historicosNormalizados));
        idsPendentes.forEach((tecnicoId) => historicoIdsCarregadosRef.current.add(tecnicoId));
        setHistoricoIdsCarregados(Array.from(historicoIdsCarregadosRef.current));
      } catch (e) {
        if (ativo) setErro(e instanceof Error ? e.message : String(e));
      } finally {
        idsPendentes.forEach((tecnicoId) => historicoIdsCarregandoRef.current.delete(tecnicoId));
        if (ativo) setHistoricoIdsCarregando(Array.from(historicoIdsCarregandoRef.current));
      }
    }

    void carregarHistoricoSelecionado();
    return () => {
      ativo = false;
    };
  }, [podeRastrear, tecnicosSelecionados]);

  if (!podeRastrear) {
    return (
      <div>
        <div className="titulo-pg"><h1>Rastreamento de técnicos</h1></div>
        <div className="msg-erro">Sem permissão para ver o mapa de localização dos técnicos.</div>
      </div>
    );
  }

  const tecnicosSelecionadosSet = new Set(tecnicosSelecionados);
  const locPorTecnico = new Map<number, TecnicoLocalizacao>();
  locs.forEach((loc) => locPorTecnico.set(loc.tecnico_id, loc));
  const locsFiltradas = locs.filter((loc) => tecnicosSelecionadosSet.has(loc.tecnico_id));
  const historicoFiltrado = historico.filter((item) => tecnicosSelecionadosSet.has(item.tecnico_id));
  const tecnicosTabela = tecnicosDisponiveis.filter((tecnico) => tecnicosSelecionadosSet.has(tecnico.tecnico_id));
  const corPorTecnico = new Map<number, string>();
  tecnicosDisponiveis.forEach((tecnico, indice) => {
    corPorTecnico.set(tecnico.tecnico_id, corTecnicoPorIndice(indice));
  });

  const historicoPorTecnico = new Map<number, TecnicoHistorico>();
  historico.forEach((item) => historicoPorTecnico.set(item.tecnico_id, item));

  const historicoIdsCarregadosSet = new Set(historicoIdsCarregados);
  const historicoIdsCarregandoSet = new Set(historicoIdsCarregando);
  const quilometragemPorTecnico = new Map<number, QuilometragemResumo | null>();
  tecnicosSelecionados.forEach((tecnicoId) => {
    const item = historicoPorTecnico.get(tecnicoId);
    quilometragemPorTecnico.set(tecnicoId, item ? calcularQuilometragemPontos(item.pontos) : null);
  });
  const tecnicosComHistoricoPendente = tecnicosSelecionados.filter((tecnicoId) => (
    !historicoIdsCarregadosSet.has(tecnicoId) || historicoIdsCarregandoSet.has(tecnicoId)
  ));

  const totaisKm = Array.from(quilometragemPorTecnico.values()).reduce((acc, atual) => ({
    hojeKm: acc.hojeKm + (atual?.hojeKm || 0),
    totalKm: acc.totalKm + (atual?.totalKm || 0),
  }), { hojeKm: 0, totalKm: 0 });

  const locsValidas = locsFiltradas.filter(coordenadaValida);
  const tecnicosComRota = historicoFiltrado.filter((item) => pontosDeRota(item).length > 1).length;
  const tecnicosComVelocidade = historicoFiltrado.filter((item) => calcularVelocidadeKmh(item.pontos) !== null).length;
  const totalEmExecucao = locsFiltradas.filter((loc) => loc.os_codigo).length;
  const textoKmHojeResumo = tecnicosComHistoricoPendente.length && !historicoFiltrado.length ? 'Calculando...' : textoKm(totaisKm.hojeKm);
  const textoKmTotalResumo = tecnicosComHistoricoPendente.length && !historicoFiltrado.length ? 'Calculando...' : textoKm(totaisKm.totalKm);
  const carregandoMapaInicial = carregando && !locs.length && !erro;
  const carregandoMapaComplementar = !carregandoMapaInicial && !!tecnicosComHistoricoPendente.length;
  const carregandoTelaInteira = !erro && (
    carregandoMapaInicial
    || (!!tecnicosSelecionados.length && !!tecnicosComHistoricoPendente.length && !historicoFiltrado.length)
  );
  const tituloCarregamentoMapa = carregandoMapaInicial
    ? 'Preparando o mapa'
    : (mostrarRota ? 'Buscando rota completa' : 'Atualizando quilometragem');
  const detalheCarregamentoMapa = carregandoMapaInicial
    ? 'Buscando a última localização enviada para montar a visualização inicial.'
    : `${tecnicosComHistoricoPendente.length} técnico${tecnicosComHistoricoPendente.length > 1 ? 's' : ''} ainda em sincronização.`;

  function toggleTecnico(tecnicoId: number) {
    setTecnicosSelecionados((atual) => {
      if (atual.includes(tecnicoId)) {
        return atual.length > 1 ? atual.filter((id) => id !== tecnicoId) : atual;
      }
      return [...atual, tecnicoId];
    });
  }

  async function toggleMapaTelaCheia() {
    const elemento = mapaCardRef.current as any;
    if (!elemento) return;
    if (document.fullscreenElement === elemento) {
      await document.exitFullscreen?.();
      setMapaTelaCheia(false);
      return;
    }
    if (elemento.requestFullscreen) {
      await elemento.requestFullscreen();
      setMapaTelaCheia(true);
      return;
    }
    setMapaTelaCheia((atual) => !atual);
  }

  return (
    <div className="rastreamento-page">
      {carregandoTelaInteira && (
        <LoadingTelaInteira
          titulo="Buscando a localização dos técnicos"
          detalhe="Estamos reunindo a última posição, a rota e a quilometragem para montar a visualização completa."
        />
      )}
      <div className="titulo-pg">
        <div>
          <h1>Rastreamento de técnicos</h1>
          <p style={{ color: 'var(--texto-suave)', margin: '4px 0 0' }}>
            Mapa em tempo real dos técnicos. A atualização fica ativa somente enquanto esta tela está aberta.
          </p>
        </div>
        <button className="btn sec sm" onClick={() => { void carregarUltimasRef.current(); }} disabled={carregando}>
          {carregando ? 'Atualizando...' : 'Atualizar agora'}
        </button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}

      <div className="card rastreamento-toolbar-card">
        <div className="rastreamento-toolbar">
          <div className="rastreamento-filtro-bloco">
            <span className="rastreamento-filtro-titulo">Técnicos no mapa</span>
            <div className="rastreamento-toolbar-topo">
              <button
                type="button"
                className="btn sec sm"
                onClick={() => setTecnicosSelecionados(tecnicosDisponiveis.map((item) => item.tecnico_id))}
                disabled={!tecnicosDisponiveis.length}
              >
                Selecionar todos
              </button>
              <span className="rastreamento-filtro-status">
                {tecnicosSelecionados.length} de {tecnicosDisponiveis.length} selecionado{tecnicosSelecionados.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="rastreamento-multiselect rastreamento-tecnicos-multiselect" role="group" aria-label="Técnicos filtrados">
              {tecnicosDisponiveis.map((tecnico) => (
                <label
                  key={tecnico.tecnico_id}
                  className={`rastreamento-chip rastreamento-chip-tecnico ${tecnicosSelecionadosSet.has(tecnico.tecnico_id) ? 'on' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={tecnicosSelecionadosSet.has(tecnico.tecnico_id)}
                    onChange={() => toggleTecnico(tecnico.tecnico_id)}
                  />
                  <span className="rastreamento-chip-cor" style={{ background: corPorTecnico.get(tecnico.tecnico_id) || '#2563eb' }} />
                  <span>
                    <strong>{tecnico.tecnico_nome}</strong>
                    <small>
                      {tecnico.os_codigo
                        ? tecnico.os_codigo
                        : `${statusTecnicoLabel(tecnico.status)}${tecnico.possui_localizacao ? '' : ' • Sem localização'}`}
                    </small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="rastreamento-filtro-bloco">
            <span className="rastreamento-filtro-titulo">Camadas do mapa</span>
            <div className="rastreamento-multiselect" role="group" aria-label="Camadas do mapa">
              <label className={`rastreamento-chip ${mostrarRota ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={mostrarRota}
                  onChange={(event) => setCamadasSelecionadas((atual) => (
                    event.target.checked
                      ? [...atual, 'rota']
                      : atual.filter((item) => item !== 'rota')
                  ))}
                />
                <span>Rota</span>
              </label>
              <label className={`rastreamento-chip ${mostrarVelocidade ? 'on' : ''}`}>
                <input
                  type="checkbox"
                  checked={mostrarVelocidade}
                  onChange={(event) => setCamadasSelecionadas((atual) => (
                    event.target.checked
                      ? [...atual, 'velocidade']
                      : atual.filter((item) => item !== 'velocidade')
                  ))}
                />
                <span>Velocidade</span>
              </label>
            </div>
          </div>

          <div className="rastreamento-filtro-bloco rastreamento-intervalo-bloco">
            <label className="rastreamento-filtro-titulo" htmlFor="rastreamento-intervalo">Intervalo de atualização</label>
            <select
              id="rastreamento-intervalo"
              value={intervaloSegundos}
              onChange={(event) => setIntervaloSegundos(normalizarIntervaloAtualizacao(Number(event.target.value)))}
            >
              {Array.from({ length: INTERVALO_MAX_SEGUNDOS - INTERVALO_MIN_SEGUNDOS + 1 }, (_, index) => INTERVALO_MIN_SEGUNDOS + index).map((valor) => (
                <option key={valor} value={valor}>{valor} segundo{valor > 1 ? 's' : ''}</option>
              ))}
            </select>
            <div className="rastreamento-intervalo-status" aria-live="polite">
              <span className="rastreamento-intervalo-pulso" style={{ animationDuration: `${Math.max(0.9, intervaloSegundos)}s` }} />
              <strong>Rastreando</strong>
              <small>a cada {intervaloSegundos}s</small>
            </div>
          </div>
        </div>

        <div className="rastreamento-toolbar-nota">
          {!!tecnicosComHistoricoPendente.length && !mostrarRota && !mostrarVelocidade && `Atualizando km dos técnicos selecionados (${tecnicosComHistoricoPendente.length}). O mapa da última posição continua ativo.`}
          {!!tecnicosComHistoricoPendente.length && mostrarRota && `Carregando a rota dos técnicos selecionados (${tecnicosComHistoricoPendente.length}). Os pinos atuais seguem visíveis.`}
          {!tecnicosComHistoricoPendente.length && mostrarRota && !tecnicosComRota && 'Nenhum técnico possui deslocamento suficiente para traçar rota no momento.'}
          {!tecnicosComHistoricoPendente.length && mostrarVelocidade && !tecnicosComVelocidade && 'Sem dados suficientes para estimar velocidade com segurança.'}
          {!tecnicosComHistoricoPendente.length && !mostrarRota && !mostrarVelocidade && 'Os pinos continuam ativos. Rota e velocidade só aparecem quando você selecionar essas camadas.'}
        </div>

        <div className="rastreamento-resumos">
          <span>Técnicos filtrados: {tecnicosSelecionados.length}</span>
          <span>Km hoje: {textoKmHojeResumo}</span>
          <span>Km total: {textoKmTotalResumo}</span>
          {mostrarRota && <span>Rotas exibidas: {tecnicosComRota}</span>}
          {mostrarVelocidade && <span>Velocidades disponíveis: {tecnicosComVelocidade}</span>}
        </div>
      </div>

      <div className="grid cols4" style={{ marginBottom: 16 }}>
        <Stat icone="tecnico" titulo="Técnicos rastreados" cor="#2563eb" valor={locsValidas.length} detalhe="com posição válida" />
        <Stat icone="relogio" titulo="Atualização" cor="#0f766e" valor={carregando ? '...' : `${intervaloSegundos}s`} detalhe="configurável nesta tela" />
        <Stat icone="chave" titulo="Em execução" cor="#d97706" valor={totalEmExecucao} detalhe="com OS ativa" />
        <Stat icone="mapa" titulo="Km hoje" cor="#0891b2" valor={textoKmHojeResumo} detalhe="somados dos técnicos filtrados" />
        <Stat icone="mapa" titulo="Km total" cor="#7c3aed" valor={textoKmTotalResumo} detalhe="histórico registrado filtrado" />
        <Stat
          icone="mapa"
          titulo="Mapa"
          cor="#4f46e5"
          valor={mostrarRota ? tecnicosComRota : 'Ativo'}
          detalhe={mostrarRota ? 'rotas desenhadas' : (atualizadoEm ? `última: ${atualizadoEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 'aguardando dados')}
        />
      </div>

      <div className={`card mapa-card ${mapaTelaCheia ? 'mapa-card-fullscreen' : ''}`} ref={mapaCardRef}>
        <div className="mapa-card-topo">
          <div>
            <h3>Mapa dos técnicos</h3>
            <p style={{ color: 'var(--texto-suave)', marginTop: -4 }}>
              Cada ponto representa a última localização enviada pelo app do técnico.
              {mostrarRota ? ' A rota usa todo o histórico disponível dos técnicos filtrados.' : ''}
              {mostrarVelocidade ? ' A velocidade é estimada a partir dos dois últimos pontos válidos do técnico.' : ''}
            </p>
          </div>
          <button type="button" className="btn sec sm mapa-card-acao" onClick={() => { void toggleMapaTelaCheia(); }}>
            {mapaTelaCheia ? 'Sair da tela cheia' : 'Abrir em tela cheia'}
          </button>
        </div>
        <MapaTecnicos
          pontos={locsFiltradas}
          historico={historicoFiltrado}
          mostrarRota={mostrarRota}
          mostrarVelocidade={mostrarVelocidade}
          corPorTecnico={corPorTecnico}
          fullscreenAtivo={mapaTelaCheia}
          carregandoInicial={carregandoMapaInicial}
          carregandoComplementar={carregandoMapaComplementar}
          tituloCarregamento={tituloCarregamentoMapa}
          detalheCarregamento={detalheCarregamentoMapa}
        />
      </div>

      <div className="card rastreamento-table-card" style={{ marginTop: 16 }}>
        <h3>Últimas posições recebidas</h3>
        <div className="tabela-wrap rastreamento-table-wrap" style={{ border: 0, boxShadow: 'none' }}>
          <table className="rastreamento-table">
            <thead>
              <tr>
                <th>Técnico</th>
                <th>Status</th>
                <th>Última atualização</th>
                <th>Precisão</th>
                <th>Km hoje</th>
                <th>Km total</th>
                {mostrarVelocidade && <th>Velocidade</th>}
                <th>OS em andamento</th>
                <th>Coordenadas</th>
              </tr>
            </thead>
            <tbody>
              {tecnicosTabela.map((tecnico) => {
                const loc = locPorTecnico.get(tecnico.tecnico_id);
                const velocidade = mostrarVelocidade ? calcularVelocidadeKmh(historicoPorTecnico.get(tecnico.tecnico_id)?.pontos || []) : null;
                const resumoKm = quilometragemPorTecnico.get(tecnico.tecnico_id) || null;
                const kmCarregando = Boolean(loc) && (historicoIdsCarregandoSet.has(tecnico.tecnico_id) || !historicoIdsCarregadosSet.has(tecnico.tecnico_id));

                return (
                  <tr key={tecnico.tecnico_id}>
                    <td>
                      <strong>{tecnico.tecnico_nome}</strong>
                      <br />
                      <small>{tecnico.tecnico_email || '-'}</small>
                    </td>
                    <td><span className="badge">{statusTecnicoLabel(tecnico.status)}</span></td>
                    <td>
                      {loc ? (
                        <>
                          {dataHoraCurta(loc.coletado_em)}
                          <br />
                          <small>{idadeLocalizacao(loc.coletado_em)}</small>
                        </>
                      ) : 'Sem localização recebida'}
                    </td>
                    <td>{loc?.precisao_metros ? `${Math.round(loc.precisao_metros)} m` : '-'}</td>
                    <td>{loc ? (kmCarregando ? 'Calculando...' : textoKm(resumoKm?.hojeKm || 0)) : '-'}</td>
                    <td>{loc ? (kmCarregando ? 'Calculando...' : textoKm(resumoKm?.totalKm || 0)) : '-'}</td>
                    {mostrarVelocidade && <td>{textoVelocidade(velocidade)}</td>}
                    <td>{loc?.os_codigo ? `${loc.os_codigo} - ${loc.os_tipo || '-'}` : '-'}</td>
                    <td className="coord-cell">{loc ? (coordenadaValida(loc) ? `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)}` : 'Coordenada inválida') : '-'}</td>
                  </tr>
                );
              })}
              {!tecnicosTabela.length && (
                <tr>
                  <td colSpan={mostrarVelocidade ? 9 : 8} className="vazio">Nenhum técnico selecionado.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
